import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../components/AuthBridge';
import { 
    User as UserIcon, Mail, MapPin, Shield, CheckCircle, Save, 
    Globe, ShieldCheck, Zap, Timer, Target, ListChecks,
    ChevronDown, Layers, Loader2, List, Clock, Archive, Info, X,
    Settings, Gauge, Activity, ShieldAlert, Sparkles, ChevronRight,
    Smartphone, Download, Share, PlusSquare, Monitor, Check, RefreshCw,
    AlertTriangle, ShieldQuestion, AlertCircle, Laptop, MessageSquare,
    Headphones, LifeBuoy
} from 'lucide-react';
import { AdUnit } from '../components/AdUnit';
import { NotificationService, NotificationItem } from '../services/notificationService';
import { SponsoredService } from '../services/sponsoredService';
import { useNavigate, useLocation } from 'react-router';
import { StorageService } from '../services/storageService';
import { FeedbackWidget } from '../components/FeedbackWidget';
import { ModuleHeader } from '../components/ModuleHeader';
import { PageLoader } from '../components/PageLoader';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { TwoFactorSecurityModal } from '../components/admin/TwoFactorSecurityModal';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { PWAInstallModal } from '../components/PWAInstallModal';

const ALL_STATES = [
    { code: 'Global', name: 'Global Standard (Universal)' },
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

export const Profile: React.FC = () => {
    const { user, activeCert, updateUser, token } = useAuth();
    const tokens = useThemeTokens();
    const navigate = useNavigate();
    const location = useLocation();
    const config = SponsoredService.getConfig();
    
    // PWA Install Lifecycle & Detection
    const {
        isInstallable,
        isInstalled: isStandalone,
        isIOS,
        isAndroid,
        isDesktop,
        showGuide: showManualGuide,
        setShowGuide: setShowManualGuide,
        install,
        openInstallFlow
    } = usePWAInstall();

    const [show2FAModal, setShow2FAModal] = useState(false);

    const [formData, setFormData] = useState({
        name: user?.name || '',
        email: user?.email || '',
        state: user?.state || 'Global',
        stateManuallySet: user?.stateManuallySet || false,
        planTier: user?.planTier || 'free',
        preferences: user?.preferences || {
            practiceTimeLimit: 120,
            targetPassingScore: 75,
            defaultQuestionCount: 50
        }
    });
    const [isSaved, setIsSaved] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [archivedAlerts, setArchivedAlerts] = useState<NotificationItem[]>([]);
    const [isArchLoading, setIsArchLoading] = useState(false);

    useEffect(() => {
        // Scroll to support section if hash is present
        if (location.hash === '#support') {
            const el = document.getElementById('support-section');
            if (el) {
                setTimeout(() => {
                    el.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, [location.hash]);

    useEffect(() => {
        if (user?.id) {
            setIsArchLoading(true);
            NotificationService.getNotifications(user.id, true).then(setArchivedAlerts).finally(() => setIsArchLoading(false));
        }
    }, [user?.id]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsUpdating(true);
        try {
            await updateUser(formData);
            setIsSaved(true);
            setTimeout(() => setIsSaved(false), 3000);
        } finally {
            setIsUpdating(false);
        }
    };

    const updatePreference = (key: string, value: number) => {
        setFormData({
            ...formData,
            preferences: {
                ...formData.preferences,
                [key]: value
            }
        });
    };

    const getAggregatedScore = (email?: string) => {
        if (!email) return 0;
        const p = StorageService.getProgress(email);
        const history = p.history || [];
        return history.length > 0 ? Math.round(history.reduce((a,b) => a+b.score, 0)/history.length) : 0;
    };

    return (
        <div className="max-w-6xl mx-auto py-8 md:py-12 animate-fade-in text-slate-900 dark:text-slate-100">
            <ModuleHeader 
                icon={Settings}
                title="My Account & Settings"
                description="Change your screen name, where you are studying, and study helper preferences. Make sure your account shows your correct state."
                badge="Settings"
                color="slate"
            />
            
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-10 gap-8">
                <div>
                    <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-none">Settings</h1>
                    <p className="text-slate-500 font-medium mt-1 text-xs md:text-base">Customize your study helper and state exam settings.</p>
                </div>
                
                <div className="flex flex-wrap items-center gap-3">
                    <button 
                        onClick={() => {
                            if (window.confirm("This will let you take the 5-question starter quiz again. Ready?")) {
                                localStorage.removeItem('elite_diagnostic_completed');
                                updateUser({ hasCompletedDiagnostic: false });
                                alert("Quiz reset. Please refresh or go back to the Dashboard.");
                            }
                        }}
                        className="px-6 py-3 bg-rose-50 border border-rose-100 rounded-xl font-black text-[9px] tracking-widest text-rose-600 hover:bg-rose-100 transition-all flex items-center gap-2 shadow-sm"
                    >
                        <ShieldAlert size={14}/> Reset Starter Quiz
                    </button>
                    <button 
                        onClick={handleSave} 
                        disabled={isUpdating}
                        className={`px-8 py-3 text-white rounded-xl font-black text-[9px] tracking-widest shadow-xl transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2 ${tokens.primaryBtn}`}
                    >
                        {isUpdating ? <Loader2 size={14} className="animate-spin"/> : <Save size={14} />} 
                        {isSaved ? 'Saved' : 'Save Changes'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* Left Partition - Identity & PWA */}
                <div className="lg:col-span-4 space-y-8">
                    <div className={`rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden group border ${
                        tokens.isWarmAcademic
                            ? 'bg-[#0D6E6E] border-[#0B5C5C]'
                            : tokens.isClinical
                            ? 'bg-gradient-to-br from-[#0B2942] via-[#0D3C5C] to-[#071F33] border-teal-500/20'
                            : tokens.isHighContrast
                            ? 'bg-black text-amber-400 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                            : tokens.isMinimal
                            ? 'bg-slate-900 border-slate-800'
                            : tokens.isVibrant
                            ? 'bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-950 border-violet-800'
                            : 'bg-slate-900 border-white/5'
                    }`}>
                        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.15)_0%,transparent_60%)]"></div>
                        <div className="relative z-10 flex flex-col items-center text-center">
                            <div className={`w-24 h-24 rounded-[2.2rem] flex items-center justify-center text-4xl font-black shadow-xl mb-6 ring-4 ring-slate-800 ${tokens.iconBg}`}>
                                {formData.name.charAt(0).toUpperCase() || '?'}
                            </div>
                            <h3 className="text-xl font-black tracking-tight truncate w-full mb-2">{formData.name}</h3>
                            <div className="flex flex-col gap-2 w-full">
                                <div className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl flex items-center justify-between">
                                    <span className="text-[8px] font-black text-slate-400 tracking-widest">Service Level</span>
                                    <span className={`text-[8px] font-black px-2 py-0.5 rounded ${formData.planTier === 'paid' ? 'bg-amber-400 text-slate-950' : 'bg-slate-700 text-slate-300'}`}>
                                        {formData.planTier === 'paid' ? 'ELITE PRO' : 'FREE TIER'}
                                    </span>
                                </div>
                                <div className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl flex items-center justify-between">
                                    <span className="text-[8px] font-black text-slate-400 tracking-widest">Jurisdiction</span>
                                    <span className="text-[8px] font-black text-indigo-400">{formData.state} Board</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-6 bg-white rounded-[2rem] border border-slate-200 space-y-2 shadow-sm">
                            <span className="text-[8px] font-black text-slate-400 tracking-widest">Avg Readiness</span>
                            <p className="text-3xl font-black text-slate-900 tabular-nums">{getAggregatedScore(user?.email)}%</p>
                        </div>
                        <div className="p-6 bg-white rounded-[2rem] border border-slate-200 space-y-2 shadow-sm">
                            <span className="text-[8px] font-black text-slate-400 tracking-widest">Audit Depth</span>
                            <p className="text-3xl font-black text-slate-900 tabular-nums">{user?.history?.length || 0}</p>
                        </div>
                    </div>

                    {config.isPwaEnabled && (
                        <div className={`bg-white dark:bg-slate-900 rounded-[2rem] p-6 sm:p-8 border-2 shadow-xl space-y-6 relative overflow-hidden group transition-all ${
                            isStandalone 
                                ? 'border-emerald-200 dark:border-emerald-800/60' 
                                : 'border-slate-200 dark:border-slate-800'
                        }`}>
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-md border border-slate-200 dark:border-slate-700 bg-[#07182b] shrink-0 flex items-center justify-center p-1">
                                    <img 
                                        src="/icons/pwa-192x192.png" 
                                        alt="App Icon" 
                                        className="w-full h-full object-contain rounded-xl"
                                        onError={(e) => { (e.target as HTMLImageElement).src = '/icons/icon.svg'; }}
                                    />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h4 className="text-sm font-black text-slate-900 dark:text-white leading-none truncate">
                                        Diamond Study Hub App
                                    </h4>
                                    <p className={`text-[9px] font-black tracking-widest uppercase mt-1.5 ${
                                        isStandalone ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'
                                    }`}>
                                        {isStandalone ? 'System App Active' : 'iOS, Android & Desktop Ready'}
                                    </p>
                                </div>
                            </div>
                            
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 leading-relaxed">
                                {isStandalone 
                                    ? 'Study Hub is running in high-performance standalone mode with instant launch and focused fullscreen layout.' 
                                    : 'Install Diamond School of Nursing directly on your iPhone, iPad, Android, or PC/Mac for a fast, distraction-free study experience.'
                                }
                            </p>
                            
                            {isStandalone ? (
                                <div className="py-3.5 px-4 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl border border-emerald-200 dark:border-emerald-800/60 flex items-center justify-center gap-2 text-emerald-700 dark:text-emerald-300">
                                    <CheckCircle size={16} />
                                    <span className="text-xs font-black tracking-wider">Device App Installed</span>
                                </div>
                            ) : (
                                <button 
                                    onClick={openInstallFlow}
                                    id="profile-pwa-install-btn"
                                    className={`w-full py-3.5 px-4 rounded-xl font-black text-xs tracking-wider shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2.5 cursor-pointer ${tokens.primaryBtn}`}
                                >
                                    <Download size={16} />
                                    <span>{isInstallable && !isIOS ? 'Install App on Device' : 'Download / Install Guide'}</span>
                                </button>
                            )}
                        </div>
                    )}

                    {/* Support & Feedback Section */}
                    <div className="space-y-4" id="support-section">
                        <div className="flex items-center gap-2 px-1">
                            <LifeBuoy size={14} className="text-indigo-500" />
                            <h4 className="text-[10px] font-black text-slate-400 tracking-widest">Support & Feedback</h4>
                        </div>
                        <FeedbackWidget />
                    </div>
                </div>
 
                {/* Right Partition - Form & Preferences */}
                <div className="lg:col-span-8 space-y-8">
                    {/* 2FA & Account Security Section - Restricted to Staff Users Only */}
                    {user?.role === 'staff' && (
                        <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-200 shadow-sm space-y-6">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-6">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl shadow-inner">
                                        <ShieldCheck size={26} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black tracking-tight text-slate-900">Two-Factor Authentication (2FA)</h3>
                                        <p className="text-[10px] font-black text-slate-400 tracking-widest mt-1">Google Authenticator & Trusted Devices</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    id="profile-open-2fa-modal-btn"
                                    onClick={() => setShow2FAModal(true)}
                                    className="px-6 py-3.5 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-2xl font-black text-xs tracking-wider shadow-md shadow-indigo-600/20 transition-all cursor-pointer active:scale-95 flex items-center gap-2.5 self-start sm:self-auto border-b-2 border-indigo-900"
                                >
                                    <ShieldCheck size={16} />
                                    <span>Manage 2FA & Devices</span>
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                                    <div>
                                        <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Protection Status</p>
                                        <p className="text-sm font-black text-slate-800 mt-0.5">
                                            {user?.twoFactorEnabled ? 'Google Authenticator Active' : 'Not Configured'}
                                        </p>
                                    </div>
                                    <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider ${
                                        user?.twoFactorEnabled 
                                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' 
                                            : 'bg-amber-100 text-amber-800 border border-amber-300'
                                    }`}>
                                        {user?.twoFactorEnabled ? 'Active' : 'Action Needed'}
                                    </span>
                                </div>

                                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                                    <div>
                                        <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Remembered Devices</p>
                                        <p className="text-sm font-black text-slate-800 mt-0.5">30-Day Trusted Tokens</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShow2FAModal(true)}
                                        className="text-[10px] font-black text-indigo-600 hover:underline uppercase tracking-wider cursor-pointer"
                                    >
                                        View Devices
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-200 shadow-sm space-y-10">
                        <div className="flex items-center gap-4 border-b border-slate-100 pb-8">
                            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl shadow-inner"><Settings size={24}/></div>
                            <div>
                                <h3 className="text-xl font-black tracking-tight text-slate-900">Logic Calibration</h3>
                                <p className="text-[10px] font-black text-slate-400 tracking-widest mt-1">Registry Identity Settings</p>
                            </div>
                        </div>
 
                        <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Legal Designation (Name)</label>
                                <div className="relative group">
                                    <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-400 transition-colors" size={18} />
                                    <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none focus:bg-white focus:ring-4 focus:ring-indigo-500/5 transition-all shadow-inner" />
                                </div>
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Primary Registry Identifier</label>
                                <div className="relative opacity-60">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                                    <input type="email" value={formData.email} disabled className="w-full pl-12 pr-4 py-4 bg-slate-100 border border-slate-200 rounded-2xl font-bold text-sm cursor-not-allowed" />
                                </div>
                            </div>
                            <div className="space-y-3 md:col-span-2">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2">
                                    <Globe size={14} className="text-medical-500" /> Active Jurisdiction
                                </label>
                                <div className="relative group">
                                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-medical-400" size={18} />
                                    <select value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} className="w-full pl-12 pr-10 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none focus:bg-white appearance-none shadow-inner transition-all">
                                        {ALL_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                                    </select>
                                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
                                </div>
                                <p className="text-[8px] text-slate-400 font-bold px-1">"Changing your state will re-calibrate all simulation items to those specific board standards."</p>
                            </div>
                        </form>
                    </div>
 
                    <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-200 shadow-sm space-y-10 animate-slide-up">
                        <div className="flex items-center gap-4 border-b border-slate-100 pb-8">
                            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl shadow-inner"><Gauge size={24}/></div>
                            <div>
                                <h3 className="text-xl font-black tracking-tight text-slate-900">Audit Parameters</h3>
                                <p className="text-[10px] font-black text-slate-400 tracking-widest mt-1">Simulation Thresholds</p>
                            </div>
                        </div>
 
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            <div className="space-y-6">
                                <div className="flex justify-between items-center px-1">
                                    <label className="text-[10px] font-black text-slate-400 tracking-widest">Integrity Threshold</label>
                                    <span className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-lg font-black text-xs">{formData.preferences.targetPassingScore}%</span>
                                </div>
                                <input type="range" min="50" max="100" value={formData.preferences.targetPassingScore} onChange={e => updatePreference('targetPassingScore', parseInt(e.target.value))} className="w-full h-1.5 bg-slate-100 rounded-full appearance-none accent-indigo-600 cursor-pointer" />
                                <p className="text-[8px] text-slate-400 font-black text-center">Minimum mark required to qualify in Simulation Lab.</p>
                            </div>
                            <div className="space-y-6">
                                <div className="flex justify-between items-center px-1">
                                    <label className="text-[10px] font-black text-slate-400 tracking-widest">Logic Depth</label>
                                    <span className="bg-medical-50 text-medical-600 px-3 py-1 rounded-lg font-black text-xs">{formData.preferences.defaultQuestionCount} Items</span>
                                </div>
                                <input type="range" min="10" max="100" step="5" value={formData.preferences.defaultQuestionCount} onChange={e => updatePreference('defaultQuestionCount', parseInt(e.target.value))} className="w-full h-1.5 bg-slate-100 rounded-full appearance-none accent-medical-500 cursor-pointer" />
                                <p className="text-[8px] font-black text-slate-400 text-center">Standard item count for non-timed practice drills.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Placement Ad */}
            <div className="mt-8">
                <AdUnit placement="profile" />
            </div>

            {/* Alert Archive Summary */}
            <section className="mt-16 space-y-6 animate-slide-up">
                <div className="flex items-center gap-4 px-2">
                    <div className="p-2.5 bg-slate-900 text-white rounded-xl shadow-lg"><Archive size={20}/></div>
                    <h2 className="text-2xl font-black tracking-tight text-slate-900">Protocol Archive</h2>
                </div>
                
                <div className="grid grid-cols-1 gap-4">
                    {isArchLoading ? (
                        <PageLoader compact message="Loading Protocol Archive..." />
                    ) : archivedAlerts.length === 0 ? (
                        <div className="p-16 bg-white rounded-[2.5rem] border-2 border-dashed border-slate-100 text-center text-slate-300 font-medium">"Archive history vacant."</div>
                    ) : (
                        archivedAlerts.map(item => (
                            <div key={item._id} className="p-6 bg-white rounded-[2rem] border border-slate-100 flex flex-col md:flex-row justify-between items-center group hover:border-indigo-400 transition-all shadow-sm">
                                <div className="flex items-center gap-6 w-full md:w-auto">
                                    <div className="p-3 bg-slate-50 text-slate-400 rounded-2xl group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-all shadow-inner shrink-0">
                                        <Info size={20}/>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h4 className="font-black text-sm text-slate-800 truncate leading-none mb-1.5">{item.title}</h4>
                                        <p className="text-xs text-slate-500 font-medium leading-relaxed line-clamp-1 group-hover:line-clamp-none transition-all">"{item.message}"</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-6 shrink-0 mt-4 md:mt-0 w-full md:w-auto justify-end border-t md:border-t-0 border-slate-50 pt-4 md:pt-0">
                                    <span className="text-[9px] font-black text-slate-300 tracking-widest tabular-nums">{new Date(item.createdAt).toLocaleDateString()}</span>
                                    <ChevronRight size={16} className="text-slate-200 group-hover:text-indigo-500 transition-all" />
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </section>

            {/* Standardized Device PWA Installation Modal */}
            <PWAInstallModal 
                isOpen={showManualGuide}
                onClose={() => setShowManualGuide(false)}
                isIOS={isIOS}
                isAndroid={isAndroid}
                isDesktop={isDesktop}
                isInstallable={isInstallable}
                onDirectInstall={install}
            />
            {/* Two-Factor Authentication Security Modal - Restricted to Staff */}
            {user?.role === 'staff' && (
                <TwoFactorSecurityModal 
                    isOpen={show2FAModal}
                    onClose={() => setShow2FAModal(false)}
                    userEmail={user?.email || ''}
                />
            )}
        </div>
    );
};
